import { css } from '@emotion/react'
import { useState, useRef, useEffect } from 'react'
import Chip from '@mui/material/Chip'

import { secondaryGrayColor } from '../../../../lib/styles/colors'
import TextField from '../TextField'
import { LabelVisibility } from '../../../../types'
import { uniqueId } from '../../../../lib/utils'

export type ChipInputProps = {
  /** Compliant aria-label */
  ariaLabel?: string
  /** Optional className applied to input parent container */
  className?: string
  /** Whether to use setTimeout to delay adding chips */
  delayBeforeAdd?: boolean
  /** Indicates field is an error state */
  error?: boolean
  /** Helper text displayed underneath the input */
  helperText?: string
  /** id of control  */
  id: string
  /** type attribute for the input field */
  inputType?: string
  /** Label of control */
  label: string
  /** Label visibility behavior */
  labelVisibility?: LabelVisibility
  /** Character limit for the input field */
  maxLength?: number
  /** name of field. Returned with onChange callback */
  name?: string
  /** The keys (`KeyboardEvent.key`) used to determine when to create a new chip. Default is 'Enter' */
  newChipKeys?: Array<string>
  /** Callback function that is called when a new chip was added (in controlled mode). */
  onAdd?: (chip: string) => void
  /** Event callback when control has blurred */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** Event callback when the chips changed */
  onChipsChange?: (chips: string[]) => void
  /** Callback function that is called when a chip is deleted */
  onDeleteChip?: (chip: string, index: number) => void
  /** Event callback when control has focused. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  /** Event callback text of the input field has changed. */
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Placeholder text */
  placeholder?: string
  /** Value of TextField */
  value?: string
}

const commonCss = css`
  .textFieldCss {
    position: relative;
    margin: 8px 8px 8px 8px;
  }

  .wrapperDivCss {
    display: inline-flex;
    flex-flow: row wrap;
    min-width: 100%;
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border: 1px solid ${secondaryGrayColor};
    border-bottom: 1px solid rgba(0, 0, 0, 0.3);

    input {
      min-width: 30%;
      flex: 1;
      padding-top: 20px;
      padding-bottom: 12px;
      border: none;
      background: transparent;
    }

    input ~ .border {
      position: relative;
      padding: 0px 2px 0px 0px;
      width: 100%;
    }
  }

  .chipContainerDiv {
    padding-top: 12px;
  }

  .chipCss {
    display: flex;
    margin: 15px 0px -4px 4px;
    background-color: ${secondaryGrayColor};
    border-radius: 32px;
    &:focus {
      background-color: ${secondaryGrayColor};
      outline: -webkit-focus-ring-color auto 1px;
    }
  }
`

const mainDiv = css`
  width: 100%;
`

const labelShrinkCss = css`
  transform: translate(11px, 10px) scale(0.75);
`

const keyCodes = {
  BACKSPACE: 8,
  DELETE: 46,
  ENTER: 13,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39
}

export const ChipInput = ({
  ariaLabel = '',
  className = '',
  delayBeforeAdd = false,
  error = false,
  helperText = '',
  id,
  inputType = '',
  label,
  labelVisibility,
  maxLength,
  name = '',
  newChipKeys = ['Enter'],
  onAdd,
  onBlur,
  onChipsChange,
  onDeleteChip,
  onFocus,
  onInputChange,
  placeholder = '',
  value = ''
}: ChipInputProps) => {
  const [focused, setFocused] = useState(false)
  const [chipLabels, setChipLabels] = useState<Array<string>>(value ? [value.trim()] : [])
  const [indexOfFocusedChip, setIndexOfFocusedChip] = useState<number | null>(null)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const chipRefs = useRef<Array<HTMLDivElement | null>>([])

  const labelCss = css`
    .labelCss {
      ${((chipLabels && chipLabels.length) || focused) && labelShrinkCss}
    }
  `

  /*
   * Update the number of chip refs when the chipLabels update
   */
  useEffect(() => {
    chipRefs.current = chipRefs.current ? chipRefs.current.slice(0, chipLabels.length) : []
  }, [chipLabels])

  /*
   * Attempt to set focus on the chip, if indexOfFocusedChip is populated
   */
  useEffect(() => {
    if (
      indexOfFocusedChip != null &&
      chipRefs.current &&
      chipRefs.current.length > indexOfFocusedChip
    ) {
      const currentChip = chipRefs.current[indexOfFocusedChip]
      if (currentChip != null) {
        currentChip.focus()
      }
    }
  }, [indexOfFocusedChip, chipRefs])

  const updateInput = (val: string) => {
    setInputValue(val)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateInput(event.target.value)
    if (onInputChange) {
      onInputChange(event)
    }
  }

  const clearInput = () => {
    updateInput('')
  }

  /*
   * Set focus on the input element (in the TextField)
   */

  const setInputFocus = () => {
    const currentEl = inputRef.current
    return currentEl && currentEl.focus()
  }

  /*
   * Set the index of the focused chip.  If not null, also focuses on that chip
   */

  const setChipFocus = (index: number | null) => {
    setIndexOfFocusedChip(index)
    if (index != null && chipRefs.current && chipRefs.current.length > index) {
      const currentChip = chipRefs.current[index]
      if (currentChip != null) {
        currentChip.focus()
      }
    }
  }

  /*
   * Update the chip labels state array, and fire the onChipsChange callback
   */
  const updateChips = (chips: string[]) => {
    setChipLabels(chips)
    if (onChipsChange) {
      onChipsChange(chips)
    }
  }

  /*
   * Clears the input field, adds the chip to the chipLabels array and creates a ref for that new chip
   */

  const handleAddChip = (chip: string) => {
    clearInput()
    if (chip.trim().length > 0) {
      updateChips([...chipLabels, chip])
      if (onAdd) {
        onAdd(chip)
      }
      return true
    }
    return false
  }

  /*
   * Removes the chip from the array of chip labels and sets the focus either to
   * the previous chip in the list, or the next chip in the list if this is the first chip,
   * or to the input field if there are no other chips
   */
  const handleDeleteChip = (chip: string, i: number) => {
    if (chipLabels) {
      const chips = chipLabels.slice()
      const deletedChip = chips.splice(i, 1)
      if (deletedChip) {
        let focusedChip = null
        if (indexOfFocusedChip != null && chips.length > 0) {
          if (indexOfFocusedChip > 0) {
            focusedChip = indexOfFocusedChip - 1
          } else {
            focusedChip = indexOfFocusedChip
          }
        }
        setChipFocus(focusedChip)
        if (focusedChip == null) {
          setInputFocus()
        }
        updateChips(chips)
      }
    }

    if (onDeleteChip) {
      onDeleteChip(chip, i)
    }
  }

  /*
   * If the key pressed was configured to create a new chip, then clear the input field (aka TextField)
   * Otherwise, update the input value in state
   */
  const handleInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (newChipKeys && newChipKeys.indexOf(event.key) >= 0) {
      clearInput()
    } else {
      updateInput((event.target as HTMLInputElement).value)
    }
  }

  /*
   * If the key pressed was configured to create a new chip, then add the new chip and set focus on the input field
   * If the key pressed was backspace or delete, update the index value of the focused chip. Can't actually set the focus on
   * the chip because it will cause the chip to get deleted by the material-ui Chip code.
   * If the key pressed was the left arrow, move the focus to the previous chip
   */
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const chips = inputValue ? [inputValue] : chipLabels
    const inputFieldValue = (event.target as HTMLInputElement).value
    if (newChipKeys && newChipKeys.indexOf(event.key) >= 0) {
      const result = handleAddChip(inputFieldValue)
      if (result !== false) {
        setInputFocus()
      }
      // preventing submit parent form or propagate Enter key to child components
      if (event.keyCode === keyCodes.ENTER) {
        event.stopPropagation()
        event.preventDefault()
      }
      return
    }

    switch (event.keyCode) {
      case keyCodes.BACKSPACE:
      case keyCodes.DELETE:
        if (inputFieldValue === '' && chipLabels.length) {
          setIndexOfFocusedChip(chipLabels.length - 1)
        }
        break
      case keyCodes.LEFT_ARROW:
        if (inputFieldValue === '' && chips.length) {
          setChipFocus(chips.length - 1)
        }
        break
      default:
        break
    }
  }

  /*
   * When the chip has focus, handle moving the focus when the left and right arrow keys are pressed
   */
  const handleChipKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const chips = inputValue ? [inputValue] : chipLabels
    const inputFieldValue = (event.target as HTMLInputElement).value
    switch (event.keyCode) {
      case keyCodes.LEFT_ARROW:
        if (indexOfFocusedChip == null && inputFieldValue === '' && chips.length) {
          setChipFocus(chips.length - 1)
        } else if (indexOfFocusedChip != null && indexOfFocusedChip > 0) {
          setChipFocus(indexOfFocusedChip - 1)
        }
        break
      case keyCodes.RIGHT_ARROW:
        if (indexOfFocusedChip != null && indexOfFocusedChip < chips.length - 1) {
          setChipFocus(indexOfFocusedChip + 1)
        } else {
          setChipFocus(null)
          setInputFocus()
        }
        break
      default:
        break
    }
  }

  /*
   * If the chip gets focus, update the indexOfFocusedChip state value.  Mainly added in case the user clicks on the chip,
   * so that we can keep track of which chip currently has focus
   *
   * TODO: This may be causing issues, causing the user to need to click on the delete icon more than once before it deletes
   */
  const handleChipFocus = (index: number) => {
    if (indexOfFocusedChip !== index) {
      setIndexOfFocusedChip(index)
    }
  }

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true)

    if (onFocus) {
      onFocus(event)
    }
  }

  /*
   * When the input field blurs, add a chip for whatever the current value is
   */
  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false)
    const inputVal = event.target.value
    if (inputVal) {
      if (delayBeforeAdd) {
        const numChipsBefore = chipLabels.length
        window.setTimeout(() => {
          const numChipsAfter = chipLabels.length
          if (numChipsBefore === numChipsAfter && inputVal) {
            handleAddChip(inputVal)
          } else {
            clearInput()
          }
        }, 150)
      } else {
        handleAddChip(inputVal)
      }
    }
    if (onBlur) {
      onBlur(event)
    }
  }

  const chipComponents = chipLabels.map((chip, i) => (
    <Chip
      aria-label={`Chip: ${chip}.  Press Delete or Backspace to remove.`}
      key={`chip-${uniqueId()}`}
      className="chipCss"
      onDelete={() => handleDeleteChip(chip, i)}
      onKeyDown={handleChipKeyDown}
      onFocus={() => handleChipFocus(i)}
      label={chip}
      ref={(el) => chipRefs.current.splice(i, 1, el)}
    />
  ))

  return (
    <div className={className} css={[mainDiv, commonCss, labelCss]}>
      <TextField
        aria-label={ariaLabel}
        childrenInputDivClassName="wrapperDivCss"
        className="textFieldCss"
        error={error}
        id={id}
        inputRef={inputRef}
        inputType={inputType}
        label={label}
        labelClassName="labelCss"
        labelVisibility={labelVisibility}
        helperText={helperText}
        maxLength={maxLength}
        name={name}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        onKeyUp={handleInputKeyUp}
        placeholder={placeholder}
        value={inputValue}
      >
        {chipComponents}
      </TextField>
    </div>
  )
}

export default ChipInput
